import { modifier } from '@styles/mixins'
import { colors } from '@styles/vars'
import React, { forwardRef, useEffect, useState } from 'react'
import ReactSelect, { NamedProps, Props } from 'react-select'
import ReactSelectCreatable from 'react-select/creatable'
import styled, { css } from 'styled-components'

interface SelectProps extends Props {
    id: string,
    name: string,
    label?: string | React.ReactNode,
    icon?: React.ReactNode,
    height?: "s" | "l",
    background?: string,
    creatable?: boolean,
    required?: boolean
    minWidth?: string
    maxWidth?: string
}

const Select = ({ id, name, label, icon, height, background, options, creatable, required, value, minWidth, maxWidth, onChange, ...props }: SelectProps, ref: React.Ref<any>) => {
    const modifiers = { icon, height, background, minWidth, maxWidth }
    const [selectValue, setSelectValue] = useState<Props["value"]>(value)
    const [inputValue, setInputValue] = useState<string>(value?.value ?? "")

    useEffect(() => {
        setInputValue(value?.value ?? "")
        setSelectValue(value)
    }, [value])

    const handleChange: Props["onChange"] = (option, action) => {
        setInputValue(option?.value ?? "")
        setSelectValue(option)
        if (onChange) onChange(option, action)
    }

    return (
        <Wrapper {...modifiers}>
            {label && <label htmlFor={id}>{label}</label>}
            <div>
                <input className="hide" tabIndex={-1} id={id} name={name} type="text" value={inputValue} onChange={() => false} required={required} />
                <SelectInput ref={ref} creatable={creatable} onChange={handleChange} className="select" classNamePrefix="select" options={options} value={selectValue} {...props} />
                {icon}
            </div>
        </Wrapper>
    )
}

interface SelectInputProps extends NamedProps {
    creatable?: boolean
}

const SelectInput = forwardRef<any, SelectInputProps>(({ creatable, ...props }: SelectInputProps, ref: React.Ref<any>) => {
    if (creatable) return <ReactSelectCreatable ref={ref} {...props} />
    return <ReactSelect ref={ref} {...props} />
})

export default forwardRef(Select) as typeof Select

/* STYLES */
type SelectModifiers = Pick<SelectProps, "icon" | "height" | "background" | "minWidth" | "maxWidth">
const Wrapper = styled.div<SelectModifiers>`
    width: 100%;
    min-width: ${props => props.minWidth || "150px"};
    max-width: ${props => props.maxWidth || "none"};

    label {
        display: block;
        font-weight: bold;
        margin-bottom: 10px;
        cursor: pointer;
    }

    .hide {
        position: absolute;
        z-index: -1;
        opacity: 0;
    }

    .select {        
        &__control {
            width: 100%;
            height: 48px;
            color: ${colors.black};
            transition: .2s ease-out;
            padding: 0 0 0 15px;
            border-radius: 5px;
            border: 0;
            font-weight: 600;
            background: ${props => props.background || colors.white};
            border: 1px solid ${colors.border};
            cursor: text;

            ${modifier("height:s", css`
                height: 30px;
                padding: 0 0 0 10px;
            `)}

            ${modifier("height:l", css`
                height: 54px;
            `)}
        }

        &__value-container {
            padding: 0;
        }

        &__placeholder {
            font-weight: 400;
            color: ${colors.darkGrey};
            opacity: 0.6;
        }

        &__indicator {
            padding: 10px;
            padding-right: 17px;
            cursor: pointer;

            ${modifier("height:l", css`
                padding: 15px;
            `)}

            ${modifier("height:s", css`
                padding: 5px;
            `)}
        }

        &__indicator, &__indicator-separator {
            transition: .2s ease-out;
        }

        &__menu {
            z-index: 2;
        }
    }

    .select__control--is-focused .select__indicator {
        color: ${colors.darkGrey};
    }

    .select__control--is-focused .select__indicator-separator {
        background: ${colors.darkGrey};
    }
`